import { AbstractModel, DateFormatConstants, DateUtil, isNullOrUndefined, TimeUtil } from '@uzurv/core';
import { autoserialize, autoserializeAs, inheritSerialization } from 'cerialize';
import moment from 'moment-timezone';
import { PaymentCollectionType } from './payment-collection-type';
import { RideMetadataValue } from './ride-metadata-value';
import { RideRequestInfo } from './ride-request-info';
import { RideRequestLocation } from './ride-request-location';
import { RideRequestType } from './ride-request-type';
import { RiderFeeDetail } from './rider-fee-detail';
import { ServiceLevel } from './service-level';

@inheritSerialization(AbstractModel)
export class RideRequest extends AbstractModel {
  @autoserialize
  itineraryId: number;

  @autoserialize
  partnerIdentifier: string;

  @autoserializeAs(RideRequestType)
  rideRequestType: RideRequestType;

  @autoserialize
  activated: boolean;

  @autoserializeAs(Date)
  activatedAt: Date;

  @autoserialize
  riderNote: string;

  @autoserializeAs(RideRequestLocation)
  pickupLocation: RideRequestLocation;

  @autoserializeAs(RideRequestLocation)
  dropoffLocation: RideRequestLocation;

  @autoserializeAs(Date)
  pickupDate: Date;

  @autoserializeAs(Date)
  dropoffDate: Date;

  @autoserialize
  dropoffDateProvided: boolean;

  @autoserialize
  distanceEstimate: number;

  @autoserialize
  durationEstimate: number;

  @autoserialize
  driverFareEstimate: number;

  @autoserialize
  tollEstimate: number;

  @autoserialize
  clientInvoiceEstimate: number;

  @autoserialize
  polylineId: number;

  @autoserialize
  passengerCount: number;

  @autoserialize
  billablePassengerCount: number;

  @autoserialize
  accepted: boolean;

  @autoserializeAs(Date)
  acceptedDate: Date;

  @autoserializeAs(Date)
  cancelledDate: Date;

  @autoserialize
  cancelledByUserId: number;

  @autoserialize
  cancellationReasonId: number;

  @autoserialize
  active: boolean;

  @autoserialize
  requestFee: number;

  @autoserialize
  incentive: number;

  @autoserialize
  transitProgramId: number;

  @autoserialize
  timeZone: string;

  @autoserialize
  favoriteDriversOnly: boolean;

  @autoserializeAs(Date)
  lastPushNotificationDate: Date;

  @autoserialize
  executeOnLyftDispatch: boolean;

  @autoserialize
  dispatchedToLyft: boolean;

  @autoserialize
  createdByUserId: number;

  @autoserialize
  activatedByUserId: number;

  @autoserializeAs(ServiceLevel)
  serviceLevel: ServiceLevel;

  @autoserializeAs(PaymentCollectionType)
  riderPaymentCollectionType: PaymentCollectionType;

  @autoserializeAs(RideMetadataValue)
  rideMetadataValues: Array<RideMetadataValue>;

  @autoserialize
  showInProviderFeed: boolean;

  @autoserialize
  preferredProviderId: number;

  @autoserialize
  assignedProviderId: number;

  @autoserialize
  isProviderAssigned: boolean;

  @autoserialize
  parentRideRequestId: number;

  @autoserialize
  riderPhoneNumber: string;

  // Local
  pickupTime: string;
  dropoffTime: string;

  rideRequestInfo: RideRequestInfo;
  riderFeeEstimate: RiderFeeDetail;
  isReturnRide: boolean;
  rideBackupAccepted: boolean;
  rideBackupEligible: boolean;

  // Ride Request Pickup Date is timezone adjusted
  private _localPickUpDate: Date;

  /**
   * Retrieve the local pick-up date which has NOT been adjusted to the time zone of the RideRequest
   *
   * @return {Date} The local pick-up date.
   */
  get localPickUpDate(): Date {
    return this._localPickUpDate;
  }

  /**
   * Sets the pick-up date in the local device time zone and adjusts RideRequest.pickupDate based on the existing time zone.
   * This should only be used during ride creation. The pick-up date coming from the backend will already be timezone adjusted.
   *
   * If this value is null or RideRequest.timeZone is null the adjusted pickUpDate will be set to null
   *
   * @param {Date} value - The local pick-up date to be set.
   */
  set localPickUpDate(value: Date) {
    this._localPickUpDate = value;

    if (!isNullOrUndefined(value) && !isNullOrUndefined(this.timeZone)) {
      this.pickupDate = TimeUtil.adjustDateToTimeZone(this.localPickUpDate, this.timeZone);
    } else {
      this.pickupDate = null;
    }
  }

  constructor() {
    super();
    this.passengerCount = 1;
    this.favoriteDriversOnly = false;
    this.billablePassengerCount = 1;
    this.rideMetadataValues = [];
    this.isReturnRide = false;
    this.rideBackupAccepted = false;
  }

  get displayTripDetails(): string {
    if (this.pickupLocation != null && this.dropoffLocation != null && (this.pickupDate != null || this.dropoffDate != null)) {
      return this.pickupLocation.display + ' <b>to</b> ' + this.dropoffLocation.display + '<br />';
    } else {
      return 'Error getting trip details';
    }
  }

  /**
   * @deprecated Use formattedPickupDisplayTime instead
   */
  get formattedDisplayTime(): string {
    if (!this.rideRequestType.onDemand) {
      return moment.tz(this.pickupDate, this.timeZone).format('h:mm A (z)');
    }
    return 'On Demand';
  }

  get formattedPickupDisplayTime(): string {
    if (!this.rideRequestType.onDemand) {
      return moment.tz(this.pickupDate, this.timeZone).format('h:mm A (z)');
    }
    return 'On Demand';
  }

  get formattedDropoffDisplayTime(): string {
    if (!this.rideRequestType.onDemand) {
      return moment.tz(this.dropoffDate, this.timeZone).format('h:mm A (z)');
    }
    return 'On Demand';
  }

  get formattedPickupTime(): string {
    if (isNullOrUndefined(this.pickupDate)) {
      return null;
    }
    return moment.tz(this.pickupDate, this.timeZone).format(DateFormatConstants.TIME);
  }

  get formattedDropoffTime(): string {
    if (isNullOrUndefined(this.dropoffDate)) {
      return null;
    }
    return moment.tz(this.dropoffDate, this.timeZone).format(DateFormatConstants.TIME);
  }

  static OnSerialized(rideRequest: RideRequest, json: any): void {
    super.OnSerialized(rideRequest, json);

    if (!isNullOrUndefined(rideRequest.pickupDate)) {
      json.pickupDate = DateUtil.serializeDate(rideRequest.pickupDate);
    }
    if (!isNullOrUndefined(rideRequest.dropoffDate)) {
      json.dropoffDate = rideRequest.dropoffDate.getTime();
    }
    if (!isNullOrUndefined(rideRequest.acceptedDate)) {
      json.acceptedDate = DateUtil.serializeDate(rideRequest.acceptedDate);
    }
    if (!isNullOrUndefined(rideRequest.cancelledDate)) {
      json.cancelledDate = DateUtil.serializeDate(rideRequest.cancelledDate);
    }

    if (!isNullOrUndefined(rideRequest.activatedAt)) {
      json.activatedAt = DateUtil.serializeDate(rideRequest.activatedAt);
    }

    if (!isNullOrUndefined(rideRequest.lastPushNotificationDate)) {
      json.lastPushNotificationDate = DateUtil.serializeDate(rideRequest.lastPushNotificationDate);
    }

    if (!isNullOrUndefined(rideRequest.riderNote)) {
      const riderNote: string = rideRequest.riderNote.trim();
      json.riderNote = riderNote.length === 0 ? null : riderNote;
    }
  }

  static OnDeserialized(instance: RideRequest, json: any): void {
    super.OnDeserialized(instance, json);

    if (!isNullOrUndefined(json['pickupDate'])) {
      instance.pickupDate = DateUtil.deserializeDate(json['pickupDate']);
    }

    if (!isNullOrUndefined(json['dropoffDate'])) {
      instance.dropoffDate = new Date(json['dropoffDate']);
    }

    if (!isNullOrUndefined(json['acceptedDate'])) {
      instance.acceptedDate = DateUtil.deserializeDate(json['acceptedDate']);
    }

    if (!isNullOrUndefined(json['cancelledDate'])) {
      instance.cancelledDate = DateUtil.deserializeDate(json['cancelledDate']);
    }

    if (!isNullOrUndefined(json['activatedAt'])) {
      instance.activatedAt = DateUtil.deserializeDate(json['activatedAt']);
    }

    if (!isNullOrUndefined(json['lastPushNotificationDate'])) {
      instance.lastPushNotificationDate = DateUtil.deserializeDate(json['lastPushNotificationDate']);
    }
  }
}
